import React, { memo, useMemo } from 'react';

import {
  arrayOf, bool, func, node, oneOfType, shape, string,
} from 'prop-types';
import { Product, ProductVariant } from '@picsio/icons';

import Tag from '../../../Tag';
import { getProductVariantKey } from '../../../../store/helpers/assets/aggregateProductVariants';
import { truncateTitle } from './helpers/truncateTitle';
import { getListingIconColor, MASTER_ICON_COLOR } from './helpers/iconColors';
import s from './styles.module.scss';

const ProductLinkChip = ({
  chipKey,
  title,
  isVariant,
  iconColor,
  disabled,
  readonly,
  closeDisabled,
  tooltipTextClose,
  onNavigate,
  onDetach,
}) => {
  const displayTitle = useMemo(() => truncateTitle(title), [title]);
  const isReadonly = readonly || disabled;

  const icon = useMemo(() => (
    isVariant ? <ProductVariant /> : <Product />
  ), [isVariant]);

  return (
    <Tag
      key={chipKey}
      type="product"
      className={s.chip}
      text={displayTitle}
      icon={icon}
      iconColor={iconColor}
      showNumber={false}
      tooltipText={title}
      tooltipTextClose={tooltipTextClose}
      hideCloseBtn={isReadonly}
      closeDisabled={!isReadonly && closeDisabled}
      onClick={isReadonly ? undefined : onNavigate}
      onClose={isReadonly ? null : onDetach}
    />
  );
};

ProductLinkChip.propTypes = {
  chipKey: string.isRequired,
  title: string.isRequired,
  isVariant: bool.isRequired,
  iconColor: string.isRequired,
  disabled: bool,
  readonly: bool,
  closeDisabled: bool,
  tooltipTextClose: oneOfType([string, func, node]).isRequired,
  onNavigate: func.isRequired,
  onDetach: func.isRequired,
};

ProductLinkChip.defaultProps = {
  disabled: false,
  readonly: false,
  closeDisabled: false,
};

const ProductLinksSection = memo(({
  title,
  titleIcon,
  products,
  variants,
  isMaster,
  disabled,
  tooltipTextClose,
  tooltipTextCloseReadOnly,
  isProductActionable,
  isProductDetachable,
  isVariantActionable,
  isVariantDetachable,
  onNavigateProduct,
  onNavigateVariant,
  onDetachProduct,
  onDetachVariant,
}) => {
  const hasContent = products.length > 0 || variants.length > 0;
  if (!hasContent) return null;

  return (
    <div className={s.section} data-qa={`product-links-${isMaster ? 'master' : 'channel'}`}>
      <div className={s.groupTitle}>
        <If condition={titleIcon}>
          <span className={s.channelIcon}>{titleIcon}</span>
        </If>
        <span className={s.text}>{title}</span>
      </div>
      <div className={s.chips}>
        {products.map((product) => {
          const chipKey = isMaster
            ? `product:${product._id}`
            : `listing:${product._id}:${product.isDraft === true ? 'draft' : 'live'}`;
          const productTitle = product.title || product._id;
          const actionable = isProductActionable(product);
          const detachable = isProductDetachable(product);
          const closeDisabled = actionable && !detachable;

          return (
            <ProductLinkChip
              key={chipKey}
              chipKey={chipKey}
              title={productTitle}
              isVariant={false}
              iconColor={isMaster ? MASTER_ICON_COLOR : getListingIconColor(product.isDraft === true)}
              disabled={disabled}
              readonly={!actionable}
              closeDisabled={closeDisabled}
              tooltipTextClose={closeDisabled ? tooltipTextCloseReadOnly : tooltipTextClose}
              onNavigate={() => onNavigateProduct(product)}
              onDetach={() => onDetachProduct(product, { isMaster })}
            />
          );
        })}
        {variants.map((variant) => {
          const baseKey = getProductVariantKey(variant);
          const chipKey = variant.type === 'listingVariant'
            ? `${baseKey}:${variant.isDraft === true ? 'draft' : 'live'}`
            : baseKey;
          const variantTitle = variant.title || variant.variantId;
          const actionable = isVariantActionable(variant);
          const detachable = isVariantDetachable(variant);
          const closeDisabled = actionable && !detachable;

          return (
            <ProductLinkChip
              key={chipKey}
              chipKey={chipKey}
              title={variantTitle}
              isVariant
              iconColor={isMaster ? MASTER_ICON_COLOR : getListingIconColor(variant.isDraft === true)}
              disabled={disabled}
              readonly={!actionable}
              closeDisabled={closeDisabled}
              tooltipTextClose={closeDisabled ? tooltipTextCloseReadOnly : tooltipTextClose}
              onNavigate={() => onNavigateVariant(variant)}
              onDetach={() => onDetachVariant(variant)}
            />
          );
        })}
      </div>
    </div>
  );
});

ProductLinksSection.propTypes = {
  title: string.isRequired,
  titleIcon: node,
  products: arrayOf(shape({})).isRequired,
  variants: arrayOf(shape({})).isRequired,
  isMaster: bool,
  disabled: bool,
  tooltipTextClose: string.isRequired,
  tooltipTextCloseReadOnly: oneOfType([string, func, node]).isRequired,
  isProductActionable: func.isRequired,
  isProductDetachable: func.isRequired,
  isVariantActionable: func.isRequired,
  isVariantDetachable: func.isRequired,
  onNavigateProduct: func.isRequired,
  onNavigateVariant: func.isRequired,
  onDetachProduct: func.isRequired,
  onDetachVariant: func.isRequired,
};

ProductLinksSection.defaultProps = {
  titleIcon: null,
  isMaster: false,
  disabled: false,
};

export default ProductLinksSection;
